/**
 * Theme manager — aplica `.dark` no <html> conforme preferência.
 * Modos: light | dark | auto (segue prefers-color-scheme).
 */
import { useEffect } from 'react'
import { create } from 'zustand'

export type ThemeMode = 'light' | 'dark' | 'auto'

interface ThemeStore {
  mode: ThemeMode
  effective: 'light' | 'dark'
  setMode: (mode: ThemeMode) => void
  setEffective: (eff: 'light' | 'dark') => void
}

const STORAGE_KEY = 'kodigilo:theme'

function readInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light'
  const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null
  if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored
  return 'light'
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function effectiveOf(mode: ThemeMode): 'light' | 'dark' {
  return mode === 'auto' ? (systemPrefersDark() ? 'dark' : 'light') : mode
}

export const useThemeStore = create<ThemeStore>((set, get) => ({
  mode: readInitialMode(),
  effective: effectiveOf(readInitialMode()),
  setMode: (mode) => {
    localStorage.setItem(STORAGE_KEY, mode)
    set({ mode, effective: effectiveOf(mode) })
    applyClass(effectiveOf(mode))
  },
  setEffective: (eff) => {
    if (get().mode === 'auto') {
      set({ effective: eff })
      applyClass(eff)
    }
  },
}))

function applyClass(eff: 'light' | 'dark') {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', eff === 'dark')
  // Atualiza meta theme-color pra status bar do iOS
  const meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
  if (meta) meta.content = eff === 'dark' ? '#0D0E10' : '#FAFAF9'
}

/** Hook a montar uma vez perto da raiz; sincroniza com mudanças do sistema. */
export function useThemeSync() {
  const setEffective = useThemeStore((s) => s.setEffective)
  const mode = useThemeStore((s) => s.mode)

  // Aplica no mount
  useEffect(() => {
    applyClass(effectiveOf(mode))
  }, [mode])

  // Listen mudanças do sistema (só importa em modo auto)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => setEffective(mq.matches ? 'dark' : 'light')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [setEffective])
}
